import { useEffect, useRef, useState } from "react";
import { TGenericIdName, TProcessStates } from "../../../lib/types/general";
import { LabSignUpFormData, TApiLab } from "../../../lib/types/labs";
import { PositionsCheckListNoFormik } from "../../common/positions-check-list";
import { TEmployeeRole } from "../../../lib/types/employee_roles";

type TPositionsSectionProps = {
    editable: boolean;
    isSomePartBeingEdited: boolean;
    userInfo: TApiLab;
    userId: string | undefined;
    workingState: TProcessStates;
    positions: TEmployeeRole[];
    setUserInfo: React.Dispatch<React.SetStateAction<TApiLab>>;
    setIsSomePartBeingEdited: (value: React.SetStateAction<boolean>) => void;
    updateLabAndLocalStorage: (
        data: LabSignUpFormData,
        name?: string
    ) => Promise<void>;
};

export const PositionsSection = ({
    editable,
    isSomePartBeingEdited,
    userInfo,
    workingState,
    userId,
    positions,
    setUserInfo,
    setIsSomePartBeingEdited,
    updateLabAndLocalStorage,
}: TPositionsSectionProps) => {
    const [isEditingPositions, setIsEditingPositions] = useState(false);
    const refPositions = useRef<any>({});

    const positionsList = (): TGenericIdName[] => {
        if (!userInfo || !userInfo?.other?.positions) return [];

        return positions.filter((pos) => {
            return (
                userInfo.other?.positions.findIndex((p) => p.id === pos.id) !==
                -1
            );
        });
    };

    const [currWorkingState, setCurrWorkingState] =
        useState<TProcessStates>("done");
    const editableOnClickHandler = (e: any) => {
        if (!editable) return;
        if (isSomePartBeingEdited) return;
        e.preventDefault();
        e.stopPropagation();
        setIsEditingPositions(true);
        setIsSomePartBeingEdited(true);
    };

    const savePositions = async () => {
        const newPositionsIds = Object.keys(refPositions.current)
            .filter((val) => refPositions.current[val])
            .map((v) => Number(v));

        const newPositions = positions.filter((pos) =>
            newPositionsIds.includes(pos.id)
        );

        setCurrWorkingState("working");
        await updateLabAndLocalStorage(
            {
                password: "",
                ...userInfo.other,
                positions: newPositions,
            },
            undefined
        );
        setUserInfo({
            ...userInfo,
            other: {
                ...userInfo.other,
                positions: newPositions,
            },
        });
        setIsEditingPositions(false);
        setIsSomePartBeingEdited(false);
        setCurrWorkingState("done");
    };

    const cancelHandler = async () => {
        setIsEditingPositions(false);
        setIsSomePartBeingEdited(false);
    };

    useEffect(() => {
        let defaultValue: {
            [key: string]: boolean;
        } = {};
        const selectedPositions = userInfo.other?.positions.reduce(
            (prev, curr) => {
                return {
                    ...prev,
                    [curr.id + ""]: true,
                };
            },
            defaultValue
        );
        refPositions.current = selectedPositions;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo]);

    return (
        <>
            {!isEditingPositions && (
                <>
                    {!!userId && (
                        <h6
                            className={
                                "fw-bold " +
                                (workingState !== "done" ||
                                currWorkingState !== "done"
                                    ? " cssLoading"
                                    : "")
                            }
                        >
                            Positions we are currently looking to fill:
                        </h6>
                    )}
                    {editable && (
                        <>
                            <div>
                                {!isSomePartBeingEdited && (
                                    <div
                                        className="fileTooltip rightEdit ms-auto mb-2"
                                        role="button"
                                        data-tooltip=" Edit "
                                        onClick={editableOnClickHandler}
                                    >
                                        <i
                                            className="fa-solid fa-pen-to-square fa-xs p-2 shadow editIcon"
                                            data-testid="edit-icon"
                                        ></i>
                                    </div>
                                )}
                            </div>
                            <h6
                                className={
                                    "fw-bold " +
                                    (workingState !== "done" ||
                                    currWorkingState !== "done"
                                        ? " cssLoading"
                                        : "")
                                }
                                onClick={(e) => {
                                    if (isSomePartBeingEdited) return;
                                    editableOnClickHandler(e);
                                }}
                                role="button"
                            >
                                Positions we are currently looking to fill:
                            </h6>
                        </>
                    )}
                    <div className="mt-3">
                        {positionsList().map((pos) => (
                            <span className="badge rounded-pill text-primary bg-white border-primary border-2 border me-2 mb-2 fs-6">
                                {" "}
                                {pos.name}
                            </span>
                        ))}
                    </div>
                </>
            )}
            {isEditingPositions && (
                <>
                    <h6
                        className={
                            "fw-bold mt-4 " +
                            (workingState !== "done" ||
                            currWorkingState !== "done"
                                ? " cssLoading"
                                : "")
                        }
                    >
                        Positions we are currently looking to fill:
                    </h6>
                    <div
                        className={
                            "" +
                            (workingState !== "done" ||
                            currWorkingState !== "done"
                                ? " cssLoading"
                                : "")
                        }
                    >
                        <div className="row p-2">
                            <PositionsCheckListNoFormik
                                roles={positions}
                                checkedList={refPositions.current}
                                refs={refPositions}
                            />
                        </div>
                        <div className="d-grid gap-3 mt-2 w-100 d-md-block">
                            <button
                                className="btn btn-primary px-5"
                                onClick={savePositions}
                            >
                                Save
                            </button>
                            <button
                                className="btn btn-danger ms-md-2 px-5"
                                onClick={cancelHandler}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};
